import * as Types from '../../declarations'
import {
	TablerIcon,
	IconBed,
	IconCalendar,
	IconCash,
	IconLicense,
	IconMap2,
	IconMountain,
	IconRoute2,
	IconShoe,
	IconStretching,
	IconTrekking,
} from '@tabler/icons-react'
import pluralize from 'pluralize'
import * as AppStore from './../../state/app-store'
import { Text } from '@mantine/core'

import SectionHeader from './components/SectionHeader'
import { convertSimpleToFrench, lowestNumOfDays } from '../../lib/helper'
import {
	formatDistance,
	formatHeight,
	lowestHighestPriceRange,
	formatMonthNumbers,
	formatNumberOrRange,
} from '../../lib/display'
import * as Selectors from './../../state/selectors'

interface HighLevelSummaryProps {
	trip: Types.DB.Models.TripAttributes
}

const IconForType = (type: string) => {
	switch (true) {
		case type === 'Length':
			return IconMap2

		case type === 'Difficulty':
			return IconStretching

		case type === 'Cost':
			return IconCash

		case type === 'When to go':
			return IconCalendar

		case type === 'Permits':
			return IconLicense

		case type === 'Guide needed?':
		case type === 'Guide mandatory?':
			return IconTrekking

		case type === 'Route':
			return IconRoute2

		case type === 'Accommodation':
			return IconBed

		case type === 'Walk in':
			return IconShoe

		case type === 'Climb':
			return IconMountain

		default:
			return IconMountain
	}
}

interface HighLevelItemProps {
	title: string
	content: React.ReactNode
	icon?: TablerIcon
}

export const HighLevelItem = ({ title, content, icon }: HighLevelItemProps) => {
	const Icon = icon || IconForType(title)
	return (
		<div className="high-level-info-item">
			<Icon size={58} />
			<div className="remainder">
				<h3>{title}</h3>
				{content}
			</div>
		</div>
	)
}

const HighLevelSummary = ({
	trip: {
		name,
		jsonData: {
			highLevelInfo,
			trekOrClimb,
			permits,
			accommodation,
			guidedLinks,
			climbDifficulty,
			// trekDifficulty,
		},
	},
}: HighLevelSummaryProps) => {
	const isTrek = trekOrClimb === 'TREK'
	const isClimb = !isTrek

	const unit = AppStore.useAppSelector(Selectors.unitSelector)

	const isTrekRouteDataComplete =
		isTrek &&
		(highLevelInfo?.trekRoute?.startPlace ?? '') !== '' &&
		(highLevelInfo?.trekRoute?.endPlace ?? '') !== '' &&
		(highLevelInfo?.trekRoute?.routeType ?? '') !== ''

	const isTotalDataComplete =
		(highLevelInfo?.distances?.total.days ?? []).length > 0 &&
		(highLevelInfo?.distances?.total.distance ?? []).length > 0 &&
		// (highLevelInfo.distances?.total.gain ?? 0) !== 0 &&
		(highLevelInfo?.distances?.total.highestPoint ?? 0) !== 0

	const isDailyDataComplete =
		(highLevelInfo?.distances?.daily.time ?? []).length > 0 &&
		(highLevelInfo?.distances?.daily.distance ?? []).length > 0 &&
		(highLevelInfo?.distances?.daily.elevationGain ?? []).length > 0

	const isWalkInDataComplete =
		highLevelInfo?.walkIn?.distanceOneWay !== 0 &&
		highLevelInfo?.walkIn?.totalGain !== 0 &&
		(highLevelInfo?.walkIn?.numDaysIn ?? []).length > 0 &&
		(highLevelInfo?.walkIn?.numDaysOut ?? []).length > 0

	const isClimbDataComplete =
		(highLevelInfo?.climb?.howManyDays ?? []).length > 0 &&
		(highLevelInfo?.climb?.elevationGainFromBaseCamp ?? 0) !== 0

	const isClimbRouteDataComplete =
		(highLevelInfo?.climbRoute?.howManyDaysTotal ?? []).length > 0 &&
		(highLevelInfo?.climbRoute?.approach ?? []).length > 0 &&
		(highLevelInfo?.climbRoute?.totalGainFromStart ?? 0) !== 0 &&
		(highLevelInfo?.climbRoute?.startPlace ?? '') !== '' &&
		(highLevelInfo?.climbRoute?.endPlace ?? '') !== ''

	const priceRangeGuided = lowestHighestPriceRange(guidedLinks || [])

	const isGoingToShowDistances =
		highLevelInfo?.distances &&
		(isTotalDataComplete || isDailyDataComplete) &&
		isTrek
	const isGoingToShowTrekRoute =
		isTrekRouteDataComplete && highLevelInfo?.trekRoute
	const isGoingToShowClimbRoute =
		isClimb && isClimbRouteDataComplete && highLevelInfo?.climbRoute

	const isGoingToShowWalkin =
		isClimb &&
		highLevelInfo?.climbRoute?.approach.includes('walk in') &&
		highLevelInfo?.walkIn &&
		isWalkInDataComplete
	const isGoingToShowClimb =
		isClimb && isClimbDataComplete && highLevelInfo?.climb
	const isGoingToShowDifficulty = isClimb && climbDifficulty
	const isGoingToShowCost =
		highLevelInfo?.cost &&
		(highLevelInfo?.cost?.solo.range[0] ?? (0 !== 0 || !!priceRangeGuided))

	const isGoingToShowWhenToGo = !!highLevelInfo?.whenToGo
	const isGoingToShowPermits = !!permits?.permits
	const isGoingToShowGuidedNeeded = !!highLevelInfo?.guideNeeded
	const isGoingToShowAccommodation = (accommodation?.types ?? []).length > 0

	const isShowingAnyOverviewTiles =
		isGoingToShowDistances ||
		isGoingToShowTrekRoute ||
		isGoingToShowClimbRoute ||
		isGoingToShowWalkin ||
		isGoingToShowClimb ||
		isGoingToShowDifficulty ||
		isGoingToShowCost ||
		isGoingToShowWhenToGo ||
		isGoingToShowPermits ||
		isGoingToShowGuidedNeeded ||
		isGoingToShowAccommodation

	if (!highLevelInfo || !isShowingAnyOverviewTiles) return <></>

	return (
		<div
			id="high-level-info-wrapper"
			className="spacing-between-sections-above"
		>
			<SectionHeader
				title={`${isTrek ? 'Trekking' : 'Climbing'} ${name}`}
				section={Types.App.DataTypes.FeedbackSection.HIGH_LEVEL_INFO}
			/>
			<Text mb="xl">
				Prepare for an adventure {isTrek ? 'treking' : 'climbing'} {name}. This
				overview provides essential details on routes, difficulty, costs, and
				the best times to go, helping you plan a successful{' '}
				{isTrek ? 'trek' : 'expedition'}.
			</Text>

			<div className="items-wrapper">
				{/* TREK ONLY */}
				{isGoingToShowDistances && (
					<HighLevelItem
						title={'Distances'}
						content={
							<ul>
								{isTotalDataComplete && (
									<li>
										<strong>
											{highLevelInfo?.distances?.total.days[0]}
											{highLevelInfo?.distances?.total.days.length === 2 && (
												<> - {highLevelInfo.distances.total.days[1]} </>
											)}
											days
										</strong>
										,{' '}
										{formatDistance(
											highLevelInfo?.distances?.total?.distance as
												| [number, number]
												| [number]
												| [],
											unit,
										)}
										{!!highLevelInfo?.distances?.total.gain && (
											<>
												, +
												{formatHeight(
													[highLevelInfo.distances.total.gain],
													unit,
												)}
											</>
										)}
										,{' '}
										{formatHeight(
											[highLevelInfo?.distances?.total?.highestPoint] as
												| [number, number]
												| [number]
												| [],
											unit,
										)}{' '}
										high point
									</li>
								)}
								{isDailyDataComplete && (
									<li>
										daily: {highLevelInfo?.distances?.daily.time[0]} -{' '}
										{highLevelInfo?.distances?.daily.time[1]} hours,{' '}
										{formatDistance(
											highLevelInfo?.distances?.daily.distance as
												| [number, number]
												| [],
											unit,
										)}
										, +
										{formatHeight(
											highLevelInfo?.distances?.daily.elevationGain as
												| [number, number]
												| [],
											unit,
										)}{' '}
										gain
									</li>
								)}
							</ul>
						}
					/>
				)}
				{/* TREK ONLY */}
				{isGoingToShowTrekRoute && !!highLevelInfo?.trekRoute && (
					<HighLevelItem
						title={'Route'}
						content={
							<ul>
								<li>{highLevelInfo.trekRoute.routeType}</li>
								{highLevelInfo.trekRoute.startPlace ===
								highLevelInfo.trekRoute.endPlace ? (
									<li>
										Starting and ending in {highLevelInfo.trekRoute.startPlace}
									</li>
								) : (
									<>
										<li>starting in {highLevelInfo.trekRoute.startPlace}</li>
										<li>ending in {highLevelInfo.trekRoute.endPlace}</li>
									</>
								)}
							</ul>
						}
					/>
				)}
				{/* CLIMB ONLY */}
				{isGoingToShowClimbRoute && !!highLevelInfo?.climbRoute && (
					<HighLevelItem
						title={'Route'}
						content={
							<ul>
								<li>
									{formatHeight(
										[highLevelInfo.climbRoute.totalGainFromStart],
										unit,
									)}{' '}
									gain over{' '}
									{formatNumberOrRange(
										highLevelInfo.climbRoute.howManyDaysTotal,
									)}{' '}
									days
								</li>

								{highLevelInfo.climbRoute.startPlace ===
								highLevelInfo.climbRoute.endPlace ? (
									<li>
										Starting and ending in {highLevelInfo.climbRoute.startPlace}
									</li>
								) : (
									<>
										<li>starting in {highLevelInfo.climbRoute.startPlace}</li>
										<li>ending in {highLevelInfo.climbRoute.endPlace}</li>
									</>
								)}

								{highLevelInfo.climbRoute.approach.length > 0 && (
									<li>
										approach: {highLevelInfo.climbRoute.approach.join(', ')}
									</li>
								)}
							</ul>
						}
					/>
				)}
				{/* CLIMB ONLY */}
				{isGoingToShowWalkin && !!highLevelInfo?.walkIn && (
					<HighLevelItem
						title={'Walk in'}
						content={
							<ul>
								<li>
									+{formatHeight([highLevelInfo.walkIn.totalGain], unit)} over a{' '}
									{formatDistance([highLevelInfo.walkIn.distanceOneWay], unit)}{' '}
									approach
								</li>
								<li>
									{formatNumberOrRange(highLevelInfo.walkIn.numDaysIn)} days in
								</li>
								<li>
									{formatNumberOrRange(highLevelInfo.walkIn.numDaysOut)} days
									out
								</li>
							</ul>
						}
					/>
				)}
				{/* CLIMB ONLY */}
				{isGoingToShowClimb && highLevelInfo?.climb && (
					<HighLevelItem
						title={'Climb'}
						content={
							<ul>
								<li>
									+
									{formatHeight(
										[highLevelInfo.climb.elevationGainFromBaseCamp],
										unit,
									)}{' '}
									over {formatNumberOrRange(highLevelInfo.climb.howManyDays)}{' '}
									{pluralize(
										'day',
										lowestNumOfDays(highLevelInfo.climb.howManyDays),
									)}
									.
								</li>
								<li>
									{highLevelInfo.climb.numberOfHighCamps} high{' '}
									{pluralize('camp', highLevelInfo.climb.numberOfHighCamps)}.
									{highLevelInfo.climb?.highCampNote && (
										<> {highLevelInfo.climb.highCampNote}</>
									)}
								</li>
							</ul>
						}
					/>
				)}

				{isGoingToShowDifficulty && (
					<>
						<HighLevelItem
							title={'Difficulty'}
							content={
								<ul>
									{climbDifficulty.simple
										.filter((diff) => !!diff)
										.sort((a, b) => a - b)
										.map((simpleScore, key) => {
											const difficulty = convertSimpleToFrench(simpleScore)
											return (
												<li key={key}>
													{/* // todo: use user set unit */}
													<strong>{difficulty.abbreviation}</strong>:{' '}
													{difficulty.shortDescription}{' '}
													<small>(alpine grade {simpleScore})</small>
												</li>
											)
										})}
								</ul>
							}
						/>
					</>
				)}

				{isGoingToShowCost && !!highLevelInfo?.cost && (
					<HighLevelItem
						title={'Cost'}
						content={
							<ul>
								{highLevelInfo.cost.solo.range.length > 0 &&
									highLevelInfo.cost.solo.range[0] !== 0 && (
										<li>
											<strong>
												{formatNumberOrRange(
													highLevelInfo.cost.solo.range,
													'$',
												)}
											</strong>{' '}
											solo
										</li>
									)}
								{priceRangeGuided && (
									<li>
										<strong>
											{formatNumberOrRange(
												priceRangeGuided?.highest
													? [priceRangeGuided.lowest, priceRangeGuided.highest]
													: [priceRangeGuided.lowest],
												'$',
											)}
										</strong>{' '}
										guided
									</li>
								)}
							</ul>
						}
					/>
				)}
				{isGoingToShowWhenToGo && !!highLevelInfo?.whenToGo && (
					<HighLevelItem
						title={'When to go'}
						content={
							<ul>
								{formatMonthNumbers(highLevelInfo.whenToGo.bestMonths).map(
									(monthRange, monthRangeIndex) => (
										<li key={monthRangeIndex}>{monthRange}</li>
									),
								)}
							</ul>
						}
					/>
				)}
				{isGoingToShowPermits && !!permits?.permits && (
					<HighLevelItem
						title={'Permits'}
						content={
							<ul>
								<li>
									{permits.permits.length}{' '}
									{pluralize('permit', permits.permits.length)}{' '}
									{pluralize('is', permits.permits.length)} needed
								</li>
								<li>
									costing around $
									{Math.ceil(
										permits.permits.reduce(
											(total, expense) => total + expense.cost,
											0,
										),
									)}
									&nbsp;total
								</li>
							</ul>
						}
					/>
				)}
				{isGoingToShowGuidedNeeded && highLevelInfo?.guideNeeded && (
					<HighLevelItem
						title={'Guide mandatory?'}
						content={
							<ul>
								<li>
									{highLevelInfo?.guideNeeded?.required ? <>yes</> : <>no</>}
								</li>
								{(highLevelInfo?.guideNeeded?.description ?? '') !== '' && (
									<li>{highLevelInfo?.guideNeeded?.description}</li>
								)}
							</ul>
						}
					/>
				)}
				{isGoingToShowAccommodation && (
					<HighLevelItem
						title={'Accommodation'}
						content={<>{accommodation?.types.join(', ')}</>}
					/>
				)}
			</div>
		</div>
	)
}

export default HighLevelSummary
